import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Sparkles, ExternalLink } from 'lucide-react';
import { apiGet, apiPut } from '../../lib/api.js';
import { Card, Field, SaveButton, isDirty, LoadingPanel } from './_shared.jsx';

const SOURCES = [
  { key: 'metaAds', label: 'Meta (Facebook/Instagram) Ads', deepLink: '', fields: [
    { name: 'pageId', label: 'Default Page ID', placeholder: '1234567890' },
    { name: 'adAccountId', label: 'Default Ad Account ID', placeholder: 'act_xxxxxxxxx' },
  ]},
  { key: 'googleAds', label: 'Google Ads', deepLink: '/google-ads', fields: [
    { name: 'customerId', label: 'Customer ID', placeholder: '123-456-7890' },
    { name: 'conversionAction', label: 'Conversion Action ID', placeholder: 'customers/123/conversionActions/456' },
  ]},
  { key: 'manualUpload', label: 'Manual CSV Upload', deepLink: '/lead-import', fields: [
    { name: 'defaultSource', label: 'Default Source Tag', placeholder: 'csv-import' },
    { name: 'autoAssign', label: 'Auto-assign on import (true/false)', placeholder: 'true' },
  ]},
  { key: 'ringy', label: 'Ringy Import', deepLink: '/lead-import', fields: [
    { name: 'apiKey', label: 'Ringy API Key', placeholder: 'ringy_…' },
    { name: 'mapPreset', label: 'Default Field-Mapping Preset', placeholder: 'ringy-default' },
  ]},
];

export default function LeadSourcesTab() {
  const [loaded, setLoaded] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet('/settings/lead-sources').then((r) => {
      if (r?.ok) {
        setLoaded(r.sources);
        setDraft(r.sources);
      }
    }).catch((e) => toast.error(e.message || 'Failed to load lead sources'));
  }, []);

  function patch(key, partial) {
    setDraft({
      ...draft,
      [key]: { ...(draft[key] || { enabled: false, config: {} }), ...partial },
    });
  }

  async function save() {
    const prev = loaded;
    setBusy(true);
    setLoaded(draft);
    try {
      const r = await apiPut('/settings/lead-sources', { sources: draft });
      if (!r?.ok) throw new Error('Save failed');
      setLoaded(r.sources);
      setDraft(r.sources);
      toast.success('Lead sources saved');
    } catch (e) {
      setLoaded(prev);
      setDraft(prev);
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  if (!draft) return <LoadingPanel label="Loading lead source config…" />;
  const dirty = isDirty(draft, loaded);

  return (
    <div className="space-y-4">
      <Card
        title="Lead Sources"
        description="Toggle which lead acquisition channels are active for this tenant. Per-source connect flows live on each channel's dedicated page."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        <div className="space-y-3">
          {SOURCES.map((s) => {
            const cur = draft[s.key] || { enabled: false, config: {} };
            return (
              <div key={s.key} className="rounded-lg border border-civix-border/60 bg-civix-panelLight">
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-4 h-4 text-civix-purpleSoft" />
                    <div>
                      <div className="font-medium text-sm">{s.label}</div>
                      {s.deepLink && (
                        <Link to={s.deepLink} className="text-xs text-white/50 inline-flex items-center gap-1 hover:text-white">
                          Manage on dedicated page <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => patch(s.key, { enabled: !cur.enabled })}
                    className={`w-12 h-6 rounded-full transition relative ${cur.enabled ? 'bg-civix-purple' : 'bg-civix-border'}`}
                    aria-label={`Toggle ${s.label}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${cur.enabled ? 'left-6' : 'left-0.5'}`} />
                  </button>
                </div>
                {cur.enabled && (
                  <div className="border-t border-civix-border/60 p-4 grid md:grid-cols-2 gap-3">
                    {s.fields.map((f) => (
                      <Field key={f.name} label={f.label}>
                        <input
                          className="input"
                          placeholder={f.placeholder}
                          value={cur.config?.[f.name] || ''}
                          onChange={(e) => patch(s.key, {
                            config: { ...(cur.config || {}), [f.name]: e.target.value },
                          })}
                        />
                      </Field>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
